/**
 * Booking: the settings behind a vendor's "book a meeting" page (design: "zCRM Booking UX";
 * replaces Pipedrive's Scheduler). Each vendor sets its look, its meeting types (title, length,
 * hosts, location, description, join link, questions) and its hosts' booking hours.
 *
 * zInspector has no place for vendor settings an API key can write yet, so these defaults ship
 * with zCRM and edits made on the settings screen are kept in this browser only (see
 * zcrm-docs/reference/booking-module.md).
 */
import { z } from "zod";

/** The lead fields a booking form can ask; each maps to a lead field (leads.ts). */
export const BOOKING_STANDARD_FIELDS = [
  { id: "mobile", label: "Mobile" },
  { id: "company", label: "Company" },
  { id: "doors", label: "Doors managed" },
  { id: "markets", label: "Market" },
  { id: "type", label: "I manage rentals as" },
  { id: "heard", label: "How did you hear about us?" },
  { id: "request", label: "What do you want to get out of this call?" },
] as const;
export type BookingFieldId = (typeof BOOKING_STANDARD_FIELDS)[number]["id"];

export const WEEKDAYS = [
  { id: 1, short: "Mon" },
  { id: 2, short: "Tue" },
  { id: 3, short: "Wed" },
  { id: 4, short: "Thu" },
  { id: 5, short: "Fri" },
  { id: 6, short: "Sat" },
  { id: 0, short: "Sun" },
] as const;

export const BOOKING_LOCATIONS = ["Video call", "Phone call"] as const;

/** Time zones offered to visitors (the vendor's own is added if missing). */
export const BOOKING_TIME_ZONES = [
  { id: "America/New_York", label: "Eastern" },
  { id: "America/Chicago", label: "Central" },
  { id: "America/Denver", label: "Mountain" },
  { id: "America/Phoenix", label: "Arizona" },
  { id: "America/Los_Angeles", label: "Pacific" },
] as const;

const time = z.string().regex(/^\d{2}:\d{2}$/, "Use HH:MM");

const questionSchema = z.object({
  field: z.enum(
    BOOKING_STANDARD_FIELDS.map((f) => f.id) as [
      BookingFieldId,
      ...BookingFieldId[],
    ],
  ),
  shown: z.boolean(),
  required: z.boolean(),
});

const customQuestionSchema = z.object({
  id: z.string().min(1),
  label: z.string().min(1, "A question needs its text"),
  required: z.boolean(),
});

const meetingTypeSchema = z.object({
  id: z.string().min(1),
  /** The page heading. */
  title: z.string().min(1, "Give the page a title"),
  /** The meeting's name on calendars ("Demo"). */
  name: z.string().min(1, "Give the meeting a name"),
  minutes: z.number().int().min(10).max(240),
  location: z.enum(BOOKING_LOCATIONS),
  description: z.string(),
  /** Sent only in the confirmation and invite, never shown before booking. */
  joinInfo: z.string(),
  hostIds: z.array(z.number().int()).min(1, "Pick at least one host"),
  questions: z.array(questionSchema),
  customQuestions: z.array(customQuestionSchema),
  /** The lead's next step once booked. */
  nextStep: z.string().min(1),
});

const hoursSchema = z.object({
  hostId: z.number().int(),
  /** Weekday (0 = Sunday) → [start, end] in the vendor's time zone; missing = not bookable. */
  days: z.record(z.string(), z.tuple([time, time])),
});

export const bookingSettingsSchema = z.object({
  brand: z.object({
    name: z.string().min(1),
    color: z.string().regex(/^#[0-9a-fA-F]{6}$/, "Use a color like #f0a018"),
    intro: z.string(),
    confirmation: z.string(),
  }),
  timeZone: z.string().min(1),
  /** Slots start every this many minutes. */
  slotStepMinutes: z.number().int().min(5).max(120),
  /** No booking sooner than this. */
  minNoticeHours: z.number().int().min(0).max(168),
  /** How far ahead slots are offered. */
  daysAhead: z.number().int().min(1).max(90),
  /** Free time kept after each meeting. */
  bufferMinutes: z.number().int().min(0).max(120),
  meetingTypes: z.array(meetingTypeSchema).min(1),
  hours: z.array(hoursSchema),
});

export type BookingSettings = z.infer<typeof bookingSettingsSchema>;
export type MeetingType = z.infer<typeof meetingTypeSchema>;
export type HostHours = z.infer<typeof hoursSchema>;

const weekdays = (start: string, end: string) =>
  Object.fromEntries(
    [1, 2, 3, 4, 5].map((day) => [String(day), [start, end] as const]),
  ) as Record<string, [string, string]>;

const allQuestions = (
  shown: BookingFieldId[],
  required: BookingFieldId[],
): MeetingType["questions"] =>
  BOOKING_STANDARD_FIELDS.map((f) => ({
    field: f.id,
    shown: shown.includes(f.id),
    required: required.includes(f.id),
  }));

/** OnSight PROS's defaults: a 20-minute sales demo and Pipedrive's 1-hour client success meeting. */
export const DEFAULT_BOOKING_SETTINGS: BookingSettings = {
  brand: {
    name: "OnSight PROS",
    color: "#f0a018",
    intro:
      "Video call or phone, your choice. You get a calendar invite right away.",
    confirmation:
      "You'll get a calendar invite right away and a reminder the day before, with a link to reschedule.",
  },
  timeZone: "America/Chicago",
  slotStepMinutes: 30,
  minNoticeHours: 4,
  daysAhead: 21,
  bufferMinutes: 15,
  meetingTypes: [
    {
      id: "demo",
      title: "Book a 20-minute demo",
      name: "Demo",
      minutes: 20,
      location: "Video call",
      description:
        "How inspections are ordered and scheduled, a sample move-in report, and pricing for your number of doors.",
      joinInfo: "[YOUR VIDEO LINK]",
      hostIds: [701, 702],
      questions: allQuestions(
        ["mobile", "company", "doors", "markets", "type", "heard", "request"],
        ["company", "heard"],
      ),
      customQuestions: [],
      nextStep: "Hold the demo",
    },
    {
      id: "client_success",
      title: "Client success meeting",
      name: "Client success meeting",
      minutes: 60,
      location: "Video call",
      description:
        "Thanks for scheduling a meeting with us. If you have any questions before we meet, reach out: we're an email or a phone call away.",
      joinInfo: "[YOUR VIDEO LINK]",
      hostIds: [703],
      questions: allQuestions(["mobile", "company", "markets", "request"], []),
      customQuestions: [],
      nextStep: "Hold the client success meeting",
    },
  ],
  hours: [
    { hostId: 701, days: weekdays("09:00", "17:00") },
    { hostId: 702, days: weekdays("08:00", "16:00") },
    { hostId: 703, days: weekdays("07:45", "15:00") },
  ],
};

/** The browser store key the settings screen saves under. */
export const BOOKING_SETTINGS_STORE_KEY = "zcrm.booking.settings";
